/**
 * Storage Encryption
 *
 * Encrypts sensitive data before storing in IndexedDB.
 * Uses a device-specific key stored in localStorage for at-rest encryption.
 *
 * This provides defense-in-depth:
 * - IndexedDB access alone doesn't expose private keys
 * - localStorage + IndexedDB access together are needed
 */

import { uint8ArrayToBase64, base64ToUint8Array } from './identity'

const STORAGE_KEY_NAME = 'crowdtalkie-storage-key'
const STORAGE_KEY_VERSION = 'v1'

/**
 * Get or create the storage encryption key
 * This key is stored in localStorage and used to encrypt IndexedDB data
 */
async function getOrCreateStorageKey(): Promise<CryptoKey> {
  // Try to load existing key
  const storedKey = localStorage.getItem(STORAGE_KEY_NAME)

  if (storedKey) {
    try {
      const keyData = JSON.parse(storedKey)
      if (keyData.version === STORAGE_KEY_VERSION && keyData.key) {
        const keyBytes = base64ToUint8Array(keyData.key)
        // Create copy to ensure proper ArrayBuffer type
        return await crypto.subtle.importKey(
          'raw',
          new Uint8Array(keyBytes).buffer,
          { name: 'AES-GCM', length: 256 },
          false,
          ['encrypt', 'decrypt']
        )
      }
    } catch {
      console.warn('[StorageEncryption] Failed to load existing key, generating new one')
    }
  }

  // Generate new key
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  )

  // Export and store in localStorage
  const keyBytes = await crypto.subtle.exportKey('raw', key)
  const keyData = {
    version: STORAGE_KEY_VERSION,
    key: uint8ArrayToBase64(new Uint8Array(keyBytes)),
    createdAt: Date.now(),
  }
  localStorage.setItem(STORAGE_KEY_NAME, JSON.stringify(keyData))

  // Return non-extractable version for use
  return await crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

// Cached storage key
let cachedStorageKey: CryptoKey | null = null

/**
 * Get the storage encryption key (cached for performance)
 */
async function getStorageKey(): Promise<CryptoKey> {
  if (!cachedStorageKey) {
    cachedStorageKey = await getOrCreateStorageKey()
  }
  return cachedStorageKey
}

/**
 * Encrypt a private key for storage
 * @param privateKey - The private key bytes to encrypt
 * @returns Encrypted data as base64 string
 */
export async function encryptPrivateKey(privateKey: Uint8Array): Promise<string> {
  const key = await getStorageKey()

  // Generate random IV
  const iv = new Uint8Array(12)
  crypto.getRandomValues(iv)

  // Encrypt - create copy to ensure proper ArrayBuffer type
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new Uint8Array(privateKey).buffer
  )

  // Combine IV + ciphertext
  const combined = new Uint8Array(iv.length + encrypted.byteLength)
  combined.set(iv)
  combined.set(new Uint8Array(encrypted), iv.length)

  return uint8ArrayToBase64(combined)
}

/**
 * Decrypt a private key from storage
 * @param encryptedKey - The encrypted data as base64 string
 * @returns Decrypted private key bytes
 */
export async function decryptPrivateKey(encryptedKey: string): Promise<Uint8Array> {
  const key = await getStorageKey()

  const combined = base64ToUint8Array(encryptedKey)

  // Extract IV and ciphertext
  const iv = combined.slice(0, 12)
  const ciphertext = combined.slice(12)

  // Decrypt
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  )

  return new Uint8Array(decrypted)
}

/**
 * Check if a private key string is encrypted
 * Encrypted keys have a specific format (longer due to IV)
 */
export function isEncryptedPrivateKey(keyString: string): boolean {
  // An encrypted key should be longer than a raw Ed25519 private key (32 bytes = 44 base64 chars)
  // Encrypted format: 12 byte IV + encrypted data + 16 byte auth tag
  // Minimum: 12 + 32 + 16 = 60 bytes = 80 base64 chars
  return keyString.length >= 70
}

/**
 * Migrate a plaintext private key to encrypted format
 */
export async function migratePrivateKey(plaintextKey: string): Promise<string> {
  // If already encrypted, return as-is
  if (isEncryptedPrivateKey(plaintextKey)) {
    return plaintextKey
  }

  // Convert from base64 and encrypt
  const keyBytes = base64ToUint8Array(plaintextKey)
  return await encryptPrivateKey(keyBytes)
}

/**
 * Load a private key, handling both encrypted and plaintext formats
 * @param storedKey - The key from storage (may be encrypted or plaintext)
 * @returns Decrypted private key bytes
 */
export async function loadPrivateKey(storedKey: string): Promise<Uint8Array> {
  if (isEncryptedPrivateKey(storedKey)) {
    return await decryptPrivateKey(storedKey)
  }
  // Legacy plaintext format
  return base64ToUint8Array(storedKey)
}
